import { useState } from "react";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {Object} Student
 * @property {number} id
 * @property {string} name
 * @property {string} email
 * @property {string} username
 * @property {string} student_code
 */

/**
 * @typedef {Object} PaginationMeta
 * @property {number} current_page
 * @property {number} per_page
 * @property {number} total
 * @property {number} last_page
 */

/**
 * @typedef {Object} AssignedStudents
 * @property {Student[]} data
 * @property {PaginationMeta} meta
 */

/**
 * @typedef {Object} TeacherDetail
 * @property {number} id
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} email
 * @property {string} tenant_id
 * @property {number} role_id
 * @property {string} status
 * @property {AssignedStudents} assigned_students
 * @property {string} teacher_code
 */

/**
 * Teacher Detail + Assigned Students Hook
 */
export const useGetTeacherDetail = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [teacherDetail, setTeacherDetail] = useState(null);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [meta, setMeta] = useState(null);

  const { axiosInstance } = useAxios();

  /**
   * Fetch teacher details by ID with students (Paginated)
   * @param {{ teacherId: string|number, page?: number, per_page?: number }} params
   */
  const getTeacherDetail = async ({ teacherId, page = 1, per_page = 10 }) => {
    setLoading(true);
    setError("");

    try {
      const query = new URLSearchParams();
      query.append("with_students", "1");
      query.append("students_per_page", per_page);
      query.append("page", page);

      const response = await axiosInstance.get(
        `${TENANT_DOMAIN}/school/admin/teacher/edit/${teacherId}?${query.toString()}`
      );

      const data = response?.data?.data;

      setTeacherDetail(data || null);
      setAssignedStudents(data?.assigned_students?.data || []);
      setMeta(data?.assigned_students?.meta || null);
    } catch (err) {
      setTeacherDetail(null);
      setAssignedStudents([]);
      setMeta(null);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    teacherDetail,
    assignedStudents,
    meta,
    getTeacherDetail,
  };
};
